import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { claritySet, getPageType } from "../utils/clarity";
import { getBotName } from "../containers/ChatBot/chatTheme";

/**
 * Keeps Clarity's custom tags in sync with the current org and page.
 *
 * The tags become filters in Clarity, so navigation can be analysed by page
 * type (portal URLs carry a per-customer hash, which would otherwise make
 * every visit a unique URL) and by whether the Ask AI entry point was even
 * available to that visitor.
 */
const useClarityTags = () => {
  const location = useLocation();
  const { organization } = useSelector((s) => s.layoutReduceData);

  useEffect(() => {
    claritySet("page_type", getPageType(location.pathname));
  }, [location.pathname]);

  // Depend on the primitives rather than the organization object: the layout
  // reducer builds a fresh object on every refetch, which would otherwise
  // re-send identical tags on each one.
  const hasOrganization = Boolean(
    organization && Object.keys(organization).length
  );
  const industrySector = organization?.industry_sector;
  const isAskAiEnabled = Boolean(
    organization?.enable_artificial_intelegence ||
      organization?.enable_predefined_chat
  );
  const botName = getBotName(organization);

  useEffect(() => {
    if (!hasOrganization) {
      return;
    }

    // claritySet ignores null/undefined, so a missing sector is simply skipped.
    claritySet("industry_sector", industrySector);
    claritySet("ask_ai_enabled", isAskAiEnabled ? "yes" : "no");

    if (isAskAiEnabled) {
      claritySet("ask_ai_bot_name", botName);
    }
  }, [hasOrganization, industrySector, isAskAiEnabled, botName]);
};

export default useClarityTags;
